import React, { useMemo } from 'react';
import { Box, Typography, circularProgressClasses, styled } from '@mui/material';
import { CircularProgress } from 'components/CircularProgress/CircularProgress';
import { CheckRounded } from '@mui/icons-material';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { CircularProgressProps } from 'components/CircularProgress/types';
import { useThemeUtils } from 'contexts/ThemeContext/hooks/useThemeUtils';
import {
  ASSESSMENT_PAGE_INDICATOR_SIZE,
  ASSESSMENT_PAGE_INDICATOR_THICKNESS,
} from 'features/assessments/constants';

interface CircularIndicatorProps
  extends Omit<CircularProgressProps, 'thickness' | 'value' | 'size'> {
  /**
   * Total number of questions on the page.
   */
  questionCount: number;
  /**
   * Number of questions completed on the page.
   */
  questionsCompleted: number;
  /**
   * Current page number.
   */
  pageNumber: number;
  /**
   * Whether this page indicator is active/selected.
   */
  isActive?: boolean;
}

const CircularIndicator = styled(CircularProgress, {
  shouldForwardProp: (props) => props !== 'isActive',
})<{ isActive?: boolean }>(({ theme }) => ({
  color: theme.palette.primary.main,
  [`& .${circularProgressClasses.circle}`]: {
    strokeLinecap: 'round',
  },
}));

const PageText = styled(Typography, { shouldForwardProp: (props) => props !== 'isActive' })<{
  isActive?: boolean;
}>(({ theme, isActive }) => ({
  color: isActive ? theme.palette.grey[600] : theme.palette.brandShade2.main,
}));

const CompletedIndicatorBox = styled(Box, {
  shouldForwardProp: (props) => props !== 'size',
})<{ size: number }>(({ size, theme }) => ({
  width: size,
  height: size,
  borderRadius: '50%',
  backgroundColor: theme.palette.green[800],
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
}));

const NotStartedIndicatorBox = styled(Box, {
  shouldForwardProp: (props) => props !== 'size',
})<{ size: number }>(({ size }) => ({
  width: size,
  height: size,
  borderRadius: size / 2,
  backgroundColor: 'transparent',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
}));

const WhiteCheck = styled(CheckRounded)(({ theme }) => ({
  color: theme.palette.common.white,
  width: 30,
  height: 30,
}));

export const AssessmentsPageCircularIndicator = ({
  pageNumber,
  questionCount,
  questionsCompleted,
  isActive,
}: CircularIndicatorProps) => {
  const { theme } = useThemeUtils();
  const { isMobile } = useDeviceType();
  const boundedProgress = useMemo(
    () => (questionCount > 0 ? Math.ceil(100 * (questionsCompleted / questionCount)) : 0),
    [questionsCompleted, questionCount],
  );

  const isPageCompleted = questionsCompleted === questionCount;

  const ispageNotStarted = questionsCompleted === 0 && questionCount > 0;

  if (!isMobile && isPageCompleted && isActive) {
    const completedIndicatorBoxSize =
      ASSESSMENT_PAGE_INDICATOR_SIZE - ASSESSMENT_PAGE_INDICATOR_THICKNESS * 2;
    return (
      <CircularIndicator
        value={100}
        size={ASSESSMENT_PAGE_INDICATOR_SIZE}
        thickness={ASSESSMENT_PAGE_INDICATOR_THICKNESS}
        backgroundStyle={{
          color: theme.palette.primary.main,
          opacity: 0.3,
        }}
        aria-hidden="true"
      >
        <CompletedIndicatorBox size={completedIndicatorBoxSize}>
          <WhiteCheck />
        </CompletedIndicatorBox>
      </CircularIndicator>
    );
  }

  if ((isMobile && isPageCompleted) || (isPageCompleted && !isActive)) {
    return (
      <CompletedIndicatorBox size={ASSESSMENT_PAGE_INDICATOR_SIZE}>
        <WhiteCheck />
      </CompletedIndicatorBox>
    );
  }

  if (ispageNotStarted) {
    return (
      <NotStartedIndicatorBox size={ASSESSMENT_PAGE_INDICATOR_SIZE}>
        <PageText
          id={`assessment__page-indicator-page-number-${pageNumber}`}
          variant="label"
          isActive={isActive}
        >
          {pageNumber}
        </PageText>
      </NotStartedIndicatorBox>
    );
  }

  return (
    <CircularIndicator
      value={boundedProgress}
      size={ASSESSMENT_PAGE_INDICATOR_SIZE}
      thickness={ASSESSMENT_PAGE_INDICATOR_THICKNESS}
      backgroundStyle={{
        color: theme.palette.primary.main,
        opacity: 0.3,
      }}
      aria-hidden="true"
    >
      <PageText
        id={`assessment__page-indicator-page-number-${pageNumber}`}
        variant="label"
        isActive={isActive}
      >
        {pageNumber}
      </PageText>
    </CircularIndicator>
  );
};
